// ===========================================================================
// Client-side media effects, applied to the LOCAL stream before it reaches the SFU.
// All self-hosted (no CDN), all user-toggled and remembered. Three effects:
//
//   #9 Noise suppression -- Web Audio DSP (high-pass + low-pass + soft gate). Works
//      with no model. RNNoise can later drop in as an AudioWorklet behind the same API.
//   #8 Background blur   -- canvas compositing via a PLUGGABLE selfie segmenter. Needs a
//      self-hosted segmentation model (bundle it and pass a segmenter); without one the
//      blur is a no-op (the rest still works).
//   #11 Filters / AR     -- canvas CSS-filter "looks". Works now. Face-mesh avatars plug
//      into the same canvas draw when a face model is supplied.
//
// The OUTPUT tracks (an audio destination, a canvas capture) are created once and stay
// stable, so toggling an effect never requires replaceTrack on the live sender. ASCII only.
// ===========================================================================

export const FILTERS = Object.freeze({
  none: '',
  mono: 'grayscale(1) contrast(1.08)',
  warm: 'sepia(0.45) saturate(1.4) hue-rotate(-10deg)',
  cool: 'saturate(1.2) hue-rotate(170deg) brightness(1.05)',
  dream: 'contrast(1.08) saturate(1.3) blur(0.4px) brightness(1.08)',
});

/**
 * Insert a noise-suppression stage between a Web Audio source and the rest of the graph.
 * Returns { output, setEnabled, enabled }. Bypassed (passthrough) until enabled.
 */
export function createAudioEnhancer(ctx, srcNode) {
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 90; // cut low rumble / hum / handling noise
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 7500; // cut hiss above the voice band
  const gate = ctx.createDynamicsCompressor();
  gate.threshold.value = -48;
  gate.knee.value = 24;
  gate.ratio.value = 4;
  gate.attack.value = 0.003;
  gate.release.value = 0.2;
  const output = ctx.createGain();
  let enabled = false;

  function wire() {
    try {
      srcNode.disconnect();
      hp.disconnect();
      lp.disconnect();
      gate.disconnect();
    } catch {
      /* nodes may not be connected yet */
    }
    if (enabled) {
      srcNode.connect(hp);
      hp.connect(lp);
      lp.connect(gate);
      gate.connect(output);
    } else {
      srcNode.connect(output);
    }
  }
  wire();

  return {
    output,
    get enabled() {
      return enabled;
    },
    setEnabled(on) {
      enabled = !!on;
      wire();
    },
  };
}

/**
 * A no-model "segmenter": a soft radial mask centered where a webcam face/torso sits
 * (opaque center -> transparent edges). Fed to the blur compositor it keeps the subject
 * sharp and blurs the surrounding background -- a real, self-hosted (pure-canvas) background
 * blur with no model or vendor. A proper ML selfie-segmentation model can replace it later
 * via the SAME (video, w, h) -> maskCanvas interface, no other changes.
 */
export function createFocusSegmenter() {
  const mask = document.createElement('canvas');
  const ctx = mask.getContext('2d');
  return (_video, w, h) => {
    if (mask.width !== w || mask.height !== h) {
      mask.width = w;
      mask.height = h;
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, w, h);
    // An ELLIPSE that follows the frame's shape. A circle sized by the short
    // edge (the old approach) leaves a portrait phone frame almost entirely
    // sharp -- which is why the blur read as "not working" on a phone.
    ctx.save();
    ctx.translate(w / 2, h * 0.44); // a touch above center, where a face sits
    ctx.scale(w * 0.34, h * 0.44);
    const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 1.25);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.55, 'rgba(255,255,255,1)'); // fully sharp core
    g.addColorStop(1, 'rgba(255,255,255,0)'); // fully blurred by the edges
    ctx.fillStyle = g;
    ctx.fillRect(-4, -4, 8, 8); // covers the frame in this scaled space
    ctx.restore();
    return mask;
  };
}

/**
 * Wrap a raw camera track in a canvas processor. The returned `track` is what you send;
 * toggling filter/blur changes what is drawn, not the track. `segmenter`, if supplied,
 * is async (video, w, h) -> a mask canvas (person opaque, background transparent).
 */
export function createVideoEnhancer(track, { segmenter = null } = {}) {
  const settings = (track.getSettings && track.getSettings()) || {};
  // A starting guess only. The canvas is re-sized to the video's REAL frame
  // size as soon as it is known (and again if it changes, e.g. a phone
  // rotating): drawing into a mismatched box stretches the picture.
  let w = settings.width || 640;
  let h = settings.height || 480;

  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.srcObject = new MediaStream([track]);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const cctx = canvas.getContext('2d');

  const bg = document.createElement('canvas');
  bg.width = w;
  bg.height = h;
  const bgctx = bg.getContext('2d');
  const fg = document.createElement('canvas');
  fg.width = w;
  fg.height = h;
  const fgctx = fg.getContext('2d');

  let filter = '';
  let blur = false;
  let running = false;
  let raf = 0;

  // Keep every canvas matched to the camera's actual frame size.
  function syncSize() {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh || (vw === w && vh === h)) return;
    w = vw;
    h = vh;
    canvas.width = w;
    canvas.height = h;
    bg.width = w;
    bg.height = h;
    fg.width = w;
    fg.height = h;
  }

  // Segmentation is far slower than the frame rate (tens of milliseconds), so
  // it runs OUT of the render loop: each frame composites the freshest mask
  // available while the next one is computed. Bodies move slowly relative to
  // 30fps, so a mask a frame or two old is invisible -- whereas awaiting the
  // model every frame would drop the video to the model's rate.
  let lastMask = null;
  let segBusy = false;
  function refreshMask() {
    if (segBusy || !segmenter) return;
    segBusy = true;
    Promise.resolve()
      .then(() => segmenter(video, w, h))
      .then((m) => {
        if (m) lastMask = m;
      })
      .catch(() => {})
      .finally(() => {
        segBusy = false;
      });
  }

  async function frame() {
    if (!running) return;
    try {
      syncSize();
      if (blur && segmenter) {
        refreshMask();
        const mask = lastMask;
        // Blurred background. Scale the radius with the frame so the effect
        // reads the same on a 480p webcam and a 1080p phone.
        bgctx.filter = `blur(${Math.max(10, Math.round(Math.min(w, h) * 0.045))}px)`;
        bgctx.drawImage(video, 0, 0, w, h);
        bgctx.filter = 'none';
        // Sharp person cut out by the mask.
        fgctx.globalCompositeOperation = 'source-over';
        fgctx.clearRect(0, 0, w, h);
        fgctx.drawImage(video, 0, 0, w, h);
        fgctx.globalCompositeOperation = 'destination-in';
        if (mask) fgctx.drawImage(mask, 0, 0, w, h);
        fgctx.globalCompositeOperation = 'source-over';
        // Compose: blurred bg, then sharp person, then the look.
        cctx.filter = filter || 'none';
        cctx.drawImage(bg, 0, 0, w, h);
        cctx.drawImage(fg, 0, 0, w, h);
        cctx.filter = 'none';
      } else {
        cctx.filter = filter || 'none';
        cctx.drawImage(video, 0, 0, w, h);
        cctx.filter = 'none';
      }
    } catch {
      /* a dropped frame is fine */
    }
    raf = requestAnimationFrame(frame);
  }

  const out = canvas.captureStream(30);
  const outTrack = out.getVideoTracks()[0];

  return {
    track: outTrack,
    hasSegmenter: !!segmenter,
    async start() {
      running = true;
      try {
        await video.play();
      } catch {
        /* autoplay may defer; frames still render once data arrives */
      }
      frame();
    },
    stop() {
      running = false;
      cancelAnimationFrame(raf);
      try {
        outTrack.stop();
        video.srcObject = null;
      } catch {
        /* ignore */
      }
    },
    setFilter(name) {
      filter = FILTERS[name] || '';
    },
    setBlur(on) {
      blur = !!on;
    },
  };
}

export default { FILTERS, createAudioEnhancer, createVideoEnhancer };
